import type { TipTapDocument, TipTapVariable } from '@poseidon/components/Input2/TipTapInput';

import { ContactSequenceTimeline } from '~/components/Sequence/ContactSequenceTimeline';
import { useContactSequenceEnrollments } from '~/components/Sequence/useContactSequenceEnrollments';

interface Props {
  teamId: string;
  /** The open contact's public id. */
  contactId: string;
  /** `@`-variables + `/`-documents for the per-step content editor (this contact's values). */
  variables?: TipTapVariable[];
  documents?: TipTapDocument[];
}

/**
 * The interact tab's LIST view of a contact's sequences: for every sequence they're actively enrolled in, the
 * campaign-style steps timeline ({@link ContactSequenceTimeline}), below the history feed — the page scroll is
 * the only scroll. Body/subject edits go to a per-contact override; the template is never touched. The Graph
 * view of the same thing is {@link ContactSequenceCanvas}. Renders nothing when the contact is in no sequence.
 */
export function ContactSequenceGraph({ teamId, contactId, variables, documents }: Props) {
  const { enrollments, saveStepContent, completeManualStep } = useContactSequenceEnrollments(teamId, contactId);
  if (enrollments.length === 0) return null;
  return (
    <div className="flex flex-col gap-6">
      {enrollments.map((enrollment) => (
        <ContactSequenceTimeline
          key={enrollment.enrollmentId}
          steps={enrollment.steps}
          overrides={enrollment.overrides}
          stepStatus={enrollment.stepStatus}
          currentNodeId={enrollment.currentNodeId}
          onSaveStepContent={saveStepContent(enrollment)}
          onCompleteManualStep={completeManualStep(enrollment)}
          variables={variables}
          documents={documents}
          paused={enrollment.pausedAt != null}
          title={enrollments.length > 1 ? enrollment.sequenceName : undefined}
        />
      ))}
    </div>
  );
}
